import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import CompareFetchMethodsModal from "../../../components/AiVisibility/DualLens/CompareFetchMethodsModal";
import DualLensJobsCard from "../../../components/AiVisibility/DualLens/DualLensJobsCard";
import { AI_VISIBILITY_LIST_PAGE_SIZE } from "../../../components/AiVisibility/aiVisibilityListConstants";
import { normalizePageUrl } from "../../../components/AiVisibility/WritingAssistance/writingAssistantUtils";
import {
	listDualLensJobs,
	regenerateDualLensJob,
	submitDualLensJob,
} from "../../../services/dualLensApi";

function getHostname(url) {
	try {
		return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
	} catch {
		return "";
	}
}

const AiVisibilityDualLens = () => {
	const navigate = useNavigate();
	const { currentWebsiteId, userLoader, websiteLoader, websiteData } =
		useContext(ProductContext);

	const [url, setUrl] = useState("");
	const [compareModalOpen, setCompareModalOpen] = useState(false);
	const [jobs, setJobs] = useState([]);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(0);
	const [loadingJobs, setLoadingJobs] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [regeneratingJobId, setRegeneratingJobId] = useState(null);

	useEffect(() => {
		setPage(0);
	}, [currentWebsiteId]);

	const fetchJobs = useCallback(async () => {
		if (!currentWebsiteId) {
			setJobs([]);
			setTotal(0);
			setHasMore(false);
			setLoadingJobs(false);
			return;
		}

		setLoadingJobs(true);
		const result = await listDualLensJobs(currentWebsiteId, {
			skip: page * AI_VISIBILITY_LIST_PAGE_SIZE,
			limit: AI_VISIBILITY_LIST_PAGE_SIZE,
		});

		if (!result.ok) {
			toast.error(result.message);
			setJobs([]);
			setTotal(0);
			setHasMore(false);
		} else {
			setJobs(result.jobs || []);
			setTotal(result.total || 0);
			setHasMore(result.hasMore);
		}
		setLoadingJobs(false);
	}, [currentWebsiteId, page]);

	useEffect(() => {
		fetchJobs();
	}, [fetchJobs]);

	const validateUrlHostname = useCallback(
		(pageUrl) => {
			const websiteUrl = websiteData?.[currentWebsiteId]?.websiteURL;
			if (!websiteUrl) return true;

			const pageHost = getHostname(pageUrl);
			const siteHost = getHostname(websiteUrl);
			if (!pageHost || !siteHost) return true;

			return pageHost === siteHost || pageHost.endsWith(`.${siteHost}`);
		},
		[currentWebsiteId, websiteData]
	);

	const handleCloseCompareModal = useCallback(() => {
		setCompareModalOpen(false);
		setUrl("");
	}, []);

	const handleSubmit = useCallback(async () => {
		if (!currentWebsiteId || !url.trim()) return;

		let normalizedUrl;
		try {
			normalizedUrl = normalizePageUrl(url.trim());
			new URL(normalizedUrl);
		} catch {
			toast.error("Please enter a valid URL");
			return;
		}

		if (!validateUrlHostname(normalizedUrl)) {
			toast.error("URL hostname must match your website");
			return;
		}

		setSubmitting(true);
		try {
			const result = await submitDualLensJob(currentWebsiteId, { url: normalizedUrl });
			if (!result.ok || !result.job?.id) {
				toast.error(result.message || "Failed to submit job");
				return;
			}

			setCompareModalOpen(false);
			setUrl("");
			setPage(0);
			await fetchJobs();
			navigate(
				`/aivisibility/dual-lens/view?jobId=${encodeURIComponent(result.job.id)}`
			);
		} catch {
			toast.error("Failed to submit job");
		} finally {
			setSubmitting(false);
		}
	}, [currentWebsiteId, url, validateUrlHostname, fetchJobs, navigate]);

	const handleRegenerate = useCallback(
		async (jobId) => {
			if (!currentWebsiteId) return;

			setRegeneratingJobId(jobId);
			try {
				const result = await regenerateDualLensJob(currentWebsiteId, jobId);
				if (!result.ok || !result.job?.id) {
					toast.error(result.message || "Failed to regenerate job");
					return;
				}

				await fetchJobs();
				navigate(
					`/aivisibility/dual-lens/view?jobId=${encodeURIComponent(result.job.id)}`
				);
			} finally {
				setRegeneratingJobId(null);
			}
		},
		[currentWebsiteId, fetchJobs, navigate]
	);

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	if (!currentWebsiteId) {
		return null;
	}

	return (
		<div className="contentBlock ai-visibility-dashboard dual-lens">
			<div className="dual-lens__page-header mb-2">
				<h2 className="dual-lens__page-title mb-0">Dual Lens View</h2>
				<Button
					color="primary"
					className="dual-lens__fetch-btn"
					onClick={() => setCompareModalOpen(true)}
				>
					Fetch By URL & Compare
				</Button>
			</div>
			<p className="dual-lens__page-subtitle text-muted mb-4">
				See the same page through three lenses - AI View (browser, JS off), Human View,
				and Human (with scroll) - as readable Markdown so you can spot what each fetch
				method captures.
			</p>
			<CompareFetchMethodsModal
				isOpen={compareModalOpen}
				onClose={handleCloseCompareModal}
				url={url}
				onUrlChange={setUrl}
				onSubmit={handleSubmit}
				submitting={submitting}
			/>
			<DualLensJobsCard
				jobs={jobs}
				loading={loadingJobs}
				total={total}
				page={page}
				pageSize={AI_VISIBILITY_LIST_PAGE_SIZE}
				hasMore={hasMore}
				onPageChange={setPage}
				onRegenerate={handleRegenerate}
				regeneratingJobId={regeneratingJobId}
				onOpenCompare={() => setCompareModalOpen(true)}
			/>
		</div>
	);
};

export default AiVisibilityDualLens;
